import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  Icon,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdDelete, MdEdit } from 'react-icons/md';
import { Link } from 'react-router-dom';
import Card from 'components/card/Card';
import Menu from 'components/menu/MainMenu';
import AmountDisplay from 'components/finance/AmountDisplay';
import { getCategoryName } from 'lib/finance';
import { useFinance } from 'contexts/FinanceContext';
import { Transaction } from 'types/finance';

interface TransactionTableProps {
  transactions: Transaction[];
  onEdit: (tx: Transaction) => void;
  onDelete: (id: string) => void;
  onToggleMarked?: (id: string) => void;
  title?: string;
  compact?: boolean;
}

export default function TransactionTable({
  transactions,
  onEdit,
  onDelete,
  onToggleMarked,
  title = 'Transactions',
  compact = false,
}: TransactionTableProps) {
  const { categories } = useFinance();
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const displayTxs = compact ? transactions.slice(0, 5) : transactions;
  const showMark = !compact && !!onToggleMarked;

  const Header = ({ children }: { children: React.ReactNode }) => (
    <Text
      justifyContent="space-between"
      align="center"
      fontSize={{ sm: '10px', lg: '12px' }}
      color="gray.400"
    >
      {children}
    </Text>
  );

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text color={textColor} fontSize="22px" fontWeight="700" lineHeight="100%">
          {title}
        </Text>
        {!compact && <Menu />}
        {compact && transactions.length > 5 && (
          <Text fontSize="sm" color="gray.500">
            Showing 5 of {transactions.length}
          </Text>
        )}
      </Flex>
      <Box>
        <Table variant="simple" color="gray.500" mb="24px" mt="12px">
          <Thead>
            <Tr>
              {showMark && (
                <Th pe="10px" borderColor={borderColor}>
                  <Header>MARK</Header>
                </Th>
              )}
              <Th pe="10px" borderColor={borderColor}>
                <Header>DATE</Header>
              </Th>
              <Th pe="10px" borderColor={borderColor}>
                <Header>TYPE</Header>
              </Th>
              <Th pe="10px" borderColor={borderColor}>
                <Header>CATEGORY</Header>
              </Th>
              <Th pe="10px" borderColor={borderColor}>
                <Header>DESCRIPTION</Header>
              </Th>
              {!compact && (
                <Th pe="10px" borderColor={borderColor}>
                  <Header>REFERENCE</Header>
                </Th>
              )}
              <Th pe="10px" borderColor={borderColor} isNumeric>
                <Header>AMOUNT</Header>
              </Th>
              {!compact && (
                <Th pe="10px" borderColor={borderColor}>
                  <Header>ACTIONS</Header>
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {displayTxs.length === 0 ? (
              <Tr>
                <Td
                  colSpan={compact ? 5 : showMark ? 8 : 7}
                  textAlign="center"
                  py="40px"
                  borderColor="transparent"
                >
                  No transactions found
                </Td>
              </Tr>
            ) : (
              displayTxs.map((tx) => (
                <Tr key={tx.id}>
                  {showMark && (
                    <Td
                      fontSize={{ sm: '14px' }}
                      minW={{ sm: '80px', md: '100px', lg: 'auto' }}
                      borderColor="transparent"
                    >
                      <Checkbox
                        isChecked={!!tx.marked}
                        colorScheme="brandScheme"
                        onChange={() => onToggleMarked?.(tx.id)}
                      />
                    </Td>
                  )}
                  <Td
                    fontSize={{ sm: '14px' }}
                    minW={{ sm: '120px', md: '150px', lg: 'auto' }}
                    borderColor="transparent"
                  >
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {tx.date}
                    </Text>
                  </Td>
                  <Td fontSize={{ sm: '14px' }} borderColor="transparent">
                    <Badge
                      colorScheme={tx.type === 'income' ? 'green' : 'red'}
                      textTransform="capitalize"
                      borderRadius="8px"
                      px="8px"
                    >
                      {tx.type}
                    </Badge>
                  </Td>
                  <Td fontSize={{ sm: '14px' }} borderColor="transparent">
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {getCategoryName(categories, tx.categoryId)}
                    </Text>
                  </Td>
                  <Td
                    fontSize={{ sm: '14px' }}
                    maxW="200px"
                    borderColor="transparent"
                  >
                    <Text
                      color={textColor}
                      fontSize="sm"
                      fontWeight="700"
                      isTruncated
                    >
                      {tx.description || '—'}
                    </Text>
                  </Td>
                  {!compact && (
                    <Td
                      fontSize={{ sm: '14px' }}
                      maxW="140px"
                      borderColor="transparent"
                    >
                      <Text
                        color="secondaryGray.600"
                        fontSize="xs"
                        fontWeight="600"
                        isTruncated
                        title={tx.reference}
                      >
                        {tx.reference || '—'}
                      </Text>
                    </Td>
                  )}
                  <Td fontSize={{ sm: '14px' }} isNumeric borderColor="transparent">
                    <AmountDisplay
                      amount={tx.amount}
                      type={tx.type}
                      showSign
                      fontSize="sm"
                      transaction={tx}
                    />
                  </Td>
                  {!compact && (
                    <Td fontSize={{ sm: '14px' }} borderColor="transparent">
                      <Flex gap="4px">
                        <IconButton
                          aria-label="Edit"
                          icon={<Icon as={MdEdit} />}
                          size="sm"
                          variant="ghost"
                          color={textColor}
                          onClick={() => onEdit(tx)}
                        />
                        <IconButton
                          aria-label="Delete"
                          icon={<Icon as={MdDelete} />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => onDelete(tx.id)}
                        />
                      </Flex>
                    </Td>
                  )}
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
      {compact && displayTxs.length > 0 && (
        <Flex px="25px" pb="20px">
          <Button
            as={Link}
            to="/admin/transactions"
            size="sm"
            variant="brand"
            borderRadius="16px"
          >
            View All
          </Button>
        </Flex>
      )}
    </Card>
  );
}
